import React, {
    useState,
    useMemo,
    useEffect,
    useCallback,
    useRef,
} from "react";
import { useTheme } from "@mui/material";
import {
    Box,
    Button,
    CircularProgress,
    InputLabel,
    TextField,
} from "@mui/material";
import { SpreadsheetView } from "../../view/SpreadsheetView";
import { bidToSheetData } from "../mappers";
import { useLocalBidState } from "../hooks/useLocalBidState";
import { useGridEventAdapters } from "../hooks/useGridEventAdapters";
import { request } from "@/Api";
import { buildAtomicPayload } from "../utils/buildAtomicPayload";
import { recalculateLocalBid } from "../utils/recalculateLocalBid";
import type { ApiBid } from "../mappers/types";

import { useDispatch } from "react-redux";
import { syncSheetData } from "../../engine/gridSlice";
import { useNavigate } from "react-router-dom";

/**
 * Columns shown in the atomic bid editor.
 * A focused subset of the full bid sheet for quick entry.
 */
const ATOMIC_COLUMNS = [
    "custom_id",
    "name",
    "days",
    "quantity",
    "rate_type",
    "vendor_currency",
    "vendor_rate",
    "markup",
    "client_budget_buffered",
    "client_quoted",
];

const INITIAL_EMPTY_BID: ApiBid = {
    id: 0,
    name: "New Local Atomic Bid",
    status: "Drafts",
    bid_type: "Standard",
    visible_columns: ["name", "client_quoted"],
    sticky_columns: ["name"],
    line_groups: [],
    line_items: [],
    addons: [],
} as any;

// ─── Bid Notes Fields ────────────────────────────────────────────────────────

type BidNoteField = "assumptions" | "inclusions" | "exclusions";

const BID_NOTE_FIELDS: { key: BidNoteField; label: string }[] = [
    { key: "assumptions", label: "Assumptions" },
    { key: "inclusions", label: "Inclusions" },
    { key: "exclusions", label: "Exclusions" },
];

interface BidNotesSectionProps {
    bid: ApiBid;
    onFieldChange: (field: BidNoteField, value: string) => void;
    readOnly?: boolean;
}

/**
 * Always-visible notes section for Assumptions, Inclusions, and Exclusions.
 * Each field auto-saves on blur to keep the bid state in sync.
 */
const BidNotesSection: React.FC<BidNotesSectionProps> = ({
    bid,
    onFieldChange,
    readOnly = false,
}) => {
    return (
        <Box
            sx={{
                px: 1.5,
                py: 1,
                display: "flex",
                flexDirection: "column",
                gap: 2,
            }}
        >
            {BID_NOTE_FIELDS.map(({ key, label }) => (
                <Box key={key}>
                    <InputLabel sx={{ mb: 0.5, fontSize: "0.8rem" }}>
                        {label}
                    </InputLabel>
                    <TextField
                        multiline
                        minRows={2}
                        maxRows={6}
                        size="small"
                        defaultValue={bid[key] ?? ""}
                        onBlur={(e) => onFieldChange(key, e.target.value)}
                        InputProps={{ readOnly }}
                        fullWidth
                    />
                </Box>
            ))}
        </Box>
    );
};

// ─── AtomicBidCreator ────────────────────────────────────────────────────────

interface AtomicBidCreatorProps {
    /** Optional initial bid data to seed the editor with. */
    initialBid?: ApiBid;
    /** Called after a successful atomic submit. */
    onSubmitSuccess?: () => void;
    /** Reports the current bid state back to the parent on every edit. */
    onBidChange?: (bid: ApiBid) => void;
    /** Which depth level (0=section, 1=category, 2=line) gets a green dot. null = none. */
    highlightDepth?: number | null;
}

export const AtomicBidCreator: React.FC<AtomicBidCreatorProps> = ({
    initialBid,
    onSubmitSuccess,
    onBidChange,
    highlightDepth: highlightDepthProp,
}) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const [submitting, setSubmitting] = useState(false);
    const [expansions, setExpansions] = useState<Record<string, boolean>>({});
    const expansionsRef = useRef(expansions);
    useEffect(() => {
        expansionsRef.current = expansions;
    }, [expansions]);

    const effectiveInitial = initialBid ?? INITIAL_EMPTY_BID;
    const {
        bid: rawBid,
        setBid: setRawBid,
        loading,
        updateField,
        addAction,
        reorderNodes,
    } = useLocalBidState(effectiveInitial);

    // Reset local state when initialBid prop changes (e.g. wizard re-preview)
    const prevInitialBidRef = useRef(initialBid);
    useEffect(() => {
        if (initialBid && initialBid !== prevInitialBidRef.current) {
            prevInitialBidRef.current = initialBid;
            setRawBid(initialBid);
        }
    }, [initialBid, setRawBid]);

    // Wrap setBid to recalculate derived fields after every edit
    const setBid = useCallback(
        (updater: ApiBid | ((prev: ApiBid | null) => ApiBid | null)) => {
            setRawBid((prev: ApiBid | null) => {
                const next =
                    typeof updater === "function" ? updater(prev) : updater;
                return next ? recalculateLocalBid(next) : next;
            });
        },
        [setRawBid],
    );

    // Recalculate on initial load and after every raw state change
    const bid = useMemo(
        () => (rawBid ? recalculateLocalBid({ ...rawBid }) : null),
        [rawBid],
    );

    // Report bid state changes to parent for persistence across tab switches.
    // Also mark the object as "known" so the initialBid-reset effect won't
    // re-trigger when the parent echoes the same object back.
    const onBidChangeRef = useRef(onBidChange);
    onBidChangeRef.current = onBidChange;
    useEffect(() => {
        if (rawBid) {
            prevInitialBidRef.current = rawBid;
            onBidChangeRef.current?.(rawBid);
        }
    }, [rawBid]);

    // When the bid changes (new groups added/removed), ensure new groups default to expanded
    useEffect(() => {
        if (!bid?.line_groups) return;
        setExpansions((prev) => {
            const next = { ...prev };
            bid.line_groups!.forEach((g: any) => {
                const key = `group-${g.id}`;
                if (next[key] === undefined) next[key] = true;
            });
            return next;
        });
    }, [bid?.line_groups]);

    // Highlight depth: use prop if explicitly provided, otherwise local toggle state
    const [localHighlightDepth, setLocalHighlightDepth] = useState<
        number | null
    >(null);
    const highlightDepth =
        highlightDepthProp !== undefined
            ? highlightDepthProp
            : localHighlightDepth;

    const currentSheets = useMemo(() => {
        if (!bid) return [];
        return [
            bidToSheetData(
                bid,
                "Estimate",
                false,
                theme,
                expansions,
                ATOMIC_COLUMNS,
                highlightDepth,
            ),
        ];
    }, [bid, theme, expansions, highlightDepth]);

    const {
        handleCellsChanged,
        handleRowAction,
        handleChevronAction,
        handleRowsReordered,
    } = useGridEventAdapters({
        currentBid: bid,
        setBid,
        currentSheets,
        updateField,
        addAction,
        reorderNodes,
        setAddonPopupGroupId: () => {},
        setAddonPopupData: () => {},
        setEditPopupGroupId: () => {},
    });

    // Wrap handleCellsChanged to detect chevron expand/collapse toggles
    const handleCellsChangedWithExpansion = useCallback(
        (changes: any[]) => {
            changes.forEach((c) => {
                const newCell = c.newCell as any;
                const prevCell = c.previousCell as any;
                if (
                    newCell?.type === "chevron" &&
                    prevCell?.type === "chevron"
                ) {
                    if (newCell.isExpanded !== prevCell.isExpanded) {
                        setExpansions((prev) => ({
                            ...prev,
                            [c.rowId]: newCell.isExpanded,
                        }));
                    }
                }
            });
            handleCellsChanged(changes);
        },
        [handleCellsChanged],
    );

    const dispatch = useDispatch();
    useEffect(() => {
        if (currentSheets.length > 0) {
            dispatch(syncSheetData({ sheet: currentSheets[0] }));
        }
    }, [currentSheets, dispatch]);

    // ─── Notes field handler ────────────────────────────────────────────
    const handleNoteFieldChange = useCallback(
        (field: BidNoteField, value: string) => {
            setBid((prev: ApiBid | null) => {
                if (!prev) return prev;
                return { ...prev, [field]: value || null };
            });
        },
        [setBid],
    );

    const handleSubmit = async () => {
        if (!bid) return;
        setSubmitting(true);
        try {
            const payload = buildAtomicPayload(bid);
            const response = await request.post("/bids/atomic", payload);
            onSubmitSuccess?.();
            navigate(`/bids/${response.data.id}`);
            setBid(INITIAL_EMPTY_BID);
        } catch (e: any) {
            console.error("Failed atomic creation:", e);
            console.error("Server error details:", e.response?.data);
            alert("Error creating bid atomically. Check console for details.");
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) return <CircularProgress />;

    return (
        <Box
            sx={{
                width: "100%",
                height: "100%",
                display: "flex",
                flexDirection: "column",
            }}
        >
            <Box
                sx={{
                    flex: 1,
                    position: "relative",
                    display: "flex",
                    maxWidth: "100vw",
                }}
            >
                <div
                    style={{ flex: 1, position: "relative", overflow: "auto" }}
                    id="react-grid-light"
                >
                    <style>{`
                        /* Allow the status dot to overflow outside the cell and pane */
                        #react-grid-light .rg-cell:has(.status-dot-cell) {
                            overflow: visible !important;
                        }

                        /* Create space inside the scroll container for the overflowing dot */
                        #react-grid-light {
                            padding-left: 35px !important;
                        }
                    `}</style>
                    <SpreadsheetView
                        onCellsChanged={handleCellsChangedWithExpansion}
                        onRowAction={handleRowAction as any}
                        onChevronAction={handleChevronAction}
                        onRowsReordered={handleRowsReordered}
                        onCellContextMenu={() => {}}
                        focusLocation={null}
                    />
                </div>
            </Box>

            {/* Assumptions / Inclusions / Exclusions */}
            {bid && (
                <BidNotesSection
                    bid={bid}
                    onFieldChange={handleNoteFieldChange}
                />
            )}

            <Box
                sx={{
                    p: 1.5,
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "center",
                }}
            >
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSubmit}
                    disabled={submitting}
                    size="small"
                >
                    {submitting ? "Saving..." : "Import Bid"}
                </Button>
            </Box>
        </Box>
    );
};
